import * as THREE from 'three';
import {
  TARGETS_COLOR_HEX,
  TARGETS_DISC_FALLBACK_COLOR_HEX,
  TARGETS_DISC_RADIUS_FACTOR,
  TARGETS_DISC_SEGMENTS,
  TARGETS_DISC_Z_OFFSET_M,
  TARGETS_DOT_COLOR_HEX,
  TARGETS_DOT_RADIUS_M,
  TARGETS_RING_INNER_RADIUS_M,
  TARGETS_RING_OUTER_RADIUS_M,
} from '../config/constants';
import { loadDiscWoodColor } from '../pell/woodTexture';

/**
 * Mid-air target: a wooden backing disc + ring + center dot, billboarded to face
 * the user. Reads as a painted roundel mounted on a wooden disc.
 * The group's +Z is the "front normal" (use `lookAt(user)` to orient it).
 *
 * Three materials are exposed via userData to let the runtime animate
 * fade out + scale (hit) or color shift to gray (miss) without rebuilding meshes.
 * The disc only ever fades (never grays) — graying a wood map looks muddy.
 */
export function createTargetMesh(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'target';

  // Backing disc — behind the ring, shared wood color map (async-upgraded).
  const discGeom = new THREE.CircleGeometry(
    TARGETS_RING_OUTER_RADIUS_M * TARGETS_DISC_RADIUS_FACTOR,
    TARGETS_DISC_SEGMENTS,
  );
  const discMat = new THREE.MeshBasicMaterial({
    color: TARGETS_DISC_FALLBACK_COLOR_HEX,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
  });
  const disc = new THREE.Mesh(discGeom, discMat);
  disc.name = 'target_disc';
  disc.position.z = TARGETS_DISC_Z_OFFSET_M;
  group.add(disc);
  void upgradeDiscWood(discMat);

  const ringGeom = new THREE.RingGeometry(
    TARGETS_RING_INNER_RADIUS_M,
    TARGETS_RING_OUTER_RADIUS_M,
    48,
  );
  const ringMat = new THREE.MeshBasicMaterial({
    color: TARGETS_COLOR_HEX,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeom, ringMat);
  ring.name = 'target_ring';
  group.add(ring);

  const dotGeom = new THREE.CircleGeometry(TARGETS_DOT_RADIUS_M, 24);
  const dotMat = new THREE.MeshBasicMaterial({
    color: TARGETS_DOT_COLOR_HEX,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
  });
  const dot = new THREE.Mesh(dotGeom, dotMat);
  dot.name = 'target_dot';
  // Slightly in front to avoid z-fighting with the ring.
  dot.position.z = 0.001;
  group.add(dot);

  group.userData['discMat'] = discMat;
  group.userData['ringMat'] = ringMat;
  group.userData['dotMat'] = dotMat;
  return group;
}

/** Swap the disc's fallback color for the shared wood map once it loads. */
async function upgradeDiscWood(mat: THREE.MeshBasicMaterial): Promise<void> {
  try {
    mat.map = await loadDiscWoodColor();
    mat.color.setHex(0xffffff);
    mat.needsUpdate = true;
  } catch {
    // Keep the solid fallback color; targets spawn/dispose continuously, so a
    // missing texture must never break the drill.
  }
}

export function disposeTargetMesh(group: THREE.Group): void {
  group.traverse((o) => {
    if ((o as THREE.Mesh).geometry) (o as THREE.Mesh).geometry.dispose();
    const mat = (o as THREE.Mesh).material;
    if (mat) {
      // Per-instance materials are disposed; their shared wood map is not.
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
  });
}
